import React, { useEffect } from "react";
import styled from "styled-components";
import { DialogActions, DialogTitle } from "@material-ui/core";

import PresentationLibraryController from "js/controllers/PresentationLibraryController";
import AppController from "js/core/AppController";
import { trackActivity } from "js/core/utilities/utilities";
import { BeautifulDialog, DialogContent } from "js/react/components/Dialogs/BaseDialog";
import { Breadcrumbs } from "js/react/components/Breadcrumbs";
import Button from "js/react/components/Button2";
import { FlexSpacer, Gap30, Gap5 } from "js/react/components/Gap";
import { FlexBox } from "js/react/components/LayoutGrid";
import { LoadingBot } from "js/react/components/LoadingBot";
import { BlueButton } from "js/react/components/UiComponents";

import GeneratingSlidesView from "js/react/views/AIGeneration/panes/GeneratePresentationPane/components/GeneratingSlidesView";
import { PresentationPromptInput } from "js/react/views/AIGeneration/panes/GeneratePresentationPane/components/PromptView";
import GeneratePresentationController, {
    GeneratePresentationState, PresentationGenerationStep
} from "js/react/views/AIGeneration/panes/GeneratePresentationPane/controllers/GeneratePresentationController";

const SubTitle = styled.span`
    font-size: 18px;
    line-height: 18px;
    font-weight: 400;
`;

type Props = {
    onSuccess?: (email: string) => void;
    closeDialog: () => void;
} & GeneratePresentationState;

export const GeneratePresentationDialog = GeneratePresentationController.withState(function GeneratePresentationDialog({
    step,
    startGenerationParams,
    closeDialog
}: Props) {
    useEffect(() => {
        GeneratePresentationController.reset(AppController.workspaceId, handleCreatePresentation);
    }, []);

    const handleCreatePresentation = async options => {
        const workspaceId = AppController.workspaceId;

        const createdPresentation = await PresentationLibraryController.createPresentation({
            workspaceId,
            name: options.name,
            theme: options.theme,
            slides: options.defaultSlides
        });
        await createdPresentation.prepareSlides();
        createdPresentation.slides.map(async slide => {
            await slide.getThumbnailUrl(createdPresentation.id, "small", 0, true);
        });

        trackActivity("Presentation", "Generate", null, null, {
            presentation_id: createdPresentation.id,
            num_slides: options.defaultSlides.length,
            from_tour: false,
        });

        AppController.showEditor({ presentationId: createdPresentation.id });

        closeDialog();
    };

    const handleStartGeneration = (prompt: string) => {
        GeneratePresentationController.generatePresentationOutline({ prompt, files: [], contextUrls: [], documentText: "" });

        trackActivity("GeneratePresentationTour", "Confirm", null, null, {
            object: "button",
            object_label: "Generate Presentation",
            action: "clicked",
            experiment_id: "5FC18A79E182FE7C764425D4F852F501",
            experiment_group_assignment: "control"
        });
    };

    const skip = () => {
        trackActivity("GeneratePresentationTour", "Skipped", null, null, {
            object: "button",
            object_label: "Skip",
            action: "clicked",
            "experiment_id": "5FC18A79E182FE7C764425D4F852F501",
            experiment_group_assignment: "control"
        });

        closeDialog();
    };

    let maxWidth: any = "md";
    if (
        step === PresentationGenerationStep.GENERATING_SLIDES ||
        step === PresentationGenerationStep.DONE
    ) {
        maxWidth = "lg";
    }

    const breadCrumbIndex = (
        step === PresentationGenerationStep.PROMPT ? 0
            : step === PresentationGenerationStep.GENERATING_OUTLINE ? 1
                : 2
    );

    return (
        <BeautifulDialog
            preventClose
            closeDialog={closeDialog}
            closeButton={false}
            maxWidth={maxWidth}
            fullWidth={maxWidth !== "lg"}
            style={{ zIndex: 10000 }}
        >
            <Gap30 />
            <Breadcrumbs
                activeIndex={breadCrumbIndex}
                crumbs={[
                    {
                        content: <span>1</span>,
                        label: "Provide a prompt",
                    },
                    {
                        content: <span>2</span>,
                        label: "Drafting a story",
                    },
                    {
                        content: <span>3</span>,
                        label: "Generating slides",
                    },
                ]}
            />
            {step === PresentationGenerationStep.PROMPT &&
                <>
                    <DialogTitle> {/* @ts-ignore() */}
                        <FlexBox column>
                            <span>Try generating a presentation with AI!</span>
                            <Gap5 />
                            <SubTitle></SubTitle>
                        </FlexBox>
                    </DialogTitle>
                    <DialogContent>
                        <PresentationPromptInput
                            handleStartGeneration={handleStartGeneration}
                            startGenerationParams={startGenerationParams}
                            startGeneratingOnEnter
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={skip}
                            label="Skip"
                            color="secondary"
                            variant="text"
                        />
                        <FlexSpacer /> {/* @ts-ignore() */}
                        <BlueButton onClick={handleStartGeneration}>Generate Presentation</BlueButton>
                    </DialogActions>
                </>
            }
            {step === PresentationGenerationStep.GENERATING_OUTLINE &&
                <>
                    <DialogTitle> {/* @ts-ignore() */}
                        <FlexBox column>
                            <span>Sit back and relax while we create a draft for you.</span>
                            <Gap5 />
                            <SubTitle>Once your draft is ready, we'll automatically advance you to the next step.</SubTitle>
                        </FlexBox>
                    </DialogTitle>
                    <DialogContent>
                        <LoadingBot
                            labels={[
                                "Gathering data...",
                                "Plotting narrative...",
                                "Curating supporting visuals...",
                            ]}
                        />
                    </DialogContent>
                </>
            }
            {(
                step === PresentationGenerationStep.GENERATING_SLIDES ||
                step === PresentationGenerationStep.DONE
            ) &&
                <>
                    <DialogTitle> {/* @ts-ignore() */}
                        <FlexBox column>
                            <span>Drumroll please... Your presentation is almost ready!</span>
                            <Gap5 />
                            <SubTitle>Update your presentation's theme and fine-tune its content once your slides are ready.</SubTitle>
                        </FlexBox>
                    </DialogTitle>
                    <DialogContent>
                        <GeneratingSlidesView
                            excludeStatusLabel
                        />
                    </DialogContent>
                </>
            }
        </BeautifulDialog>
    );
});
